import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import WebService from "../Services/WebService";
import { useAuth } from "./AuthContext";

interface CartContextType {
  cartItemCount: number;
  refreshCartCount: () => Promise<void>;
  setCartItemCount: (count: number) => void;
}

const CartContext = createContext<CartContextType>({
  cartItemCount: 0,
  refreshCartCount: async () => {},
  setCartItemCount: () => {},
});

const countItems = (items: { quantity?: number }[] | null | undefined): number => {
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [cartItemCount, setCartItemCount] = useState(0);

  const refreshCartCount = useCallback(async () => {
    if (!isAuthenticated) {
      setCartItemCount(0);
      return;
    }
    try {
      const res = await WebService.getAPI<{ data: { quantity?: number }[] }>({
        action: "/v1/door-management/cart",
      });
      setCartItemCount(countItems(res.data));
    } catch {
      // Keep last known count on transient failures
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshCartCount();
    } else {
      setCartItemCount(0);
    }
  }, [isAuthenticated, refreshCartCount]);

  return (
    <CartContext.Provider
      value={{ cartItemCount, refreshCartCount, setCartItemCount }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
